import { useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import {
  useSourceExecutions,
  type SourceExecution,
} from "@/api/sourceExecutions";
import type { Flavor } from "@/api/sources";
import { DataTable, type ColumnDef } from "@/components/data-table/DataTable";
import { DataTableFacetedFilter } from "@/components/data-table/DataTableFacetedFilter";
import { useDataTableState } from "@/components/data-table/useDataTableState";
import {
  itemEntity,
  locationEntity,
} from "@/components/entity-select/entities";
import { EntityFilter } from "@/components/entity-select/EntityFilter";
import { PageHeader } from "@/components/page-header";
import { Badge } from "@/components/ui/badge";
import { formatDate, formatNumber } from "@/utils/formatters";

// The "Overdue" filter is a single on/off option, modeled as {id, name} to
// fit the faceted-filter component.
type OverdueOption = { id: number; name: string };

export default function ExecutionsPage() {
  const { t } = useTranslation();
  const [overdue, setOverdue] = useState<OverdueOption | null>(null);
  const [producesItem, setProducesItem] = useState<number[]>([]);
  const [location, setLocation] = useState<number[]>([]);
  const [flavor, setFlavor] = useState<Flavor[]>([]);

  const ds = useDataTableState({
    defaultSort: { id: "dueDate", desc: true },
    defaultPageSize: 50,
    filterDeps: [overdue, producesItem, location, flavor],
  });

  // Supply Orders = incoming supply still open. Completed orders are history
  // and never shown here; the facet narrows further to just the overdue ones.
  const query = useSourceExecutions({
    search: ds.debouncedSearch || undefined,
    ordering: ds.orderingParam,
    completed: false,
    overdue: overdue ? true : undefined,
    producesItem: producesItem.length ? producesItem : undefined,
    location: location.length ? location : undefined,
    flavor: flavor.length ? flavor : undefined,
    limit: ds.limit,
    offset: ds.offset,
  });

  const overdueOptions: OverdueOption[] = [
    { id: 1, name: t("executions.overdue") },
  ];

  const FLAVORS = ["purchased", "manufactured", "transferred"] as const;
  const flavorOptions = FLAVORS.map((k) => ({
    id: k,
    name: t(`sources.flavor.${k}`),
  }));

  const columns = useMemo<ColumnDef<SourceExecution>[]>(
    () => [
      {
        accessorKey: "code",
        header: t("executions.columnCode"),
        size: 140,
        enableSorting: true,
      },
      {
        id: "source",
        header: t("executions.columnSource"),
        cell: ({ row }) => {
          const s = row.original.source;
          return (
            <div className="flex flex-col">
              <span className="flex items-center gap-2 font-medium">
                {s.producesItemName} @ {s.locationName}
                <Badge variant="secondary">
                  {t(`sources.flavor.${s.flavor}`)}
                </Badge>
              </span>
              <span className="text-muted-foreground text-xs">{s.name}</span>
            </div>
          );
        },
      },
      {
        accessorKey: "quantity",
        header: t("executions.columnQuantity"),
        size: 100,
        enableSorting: true,
        cell: ({ row }) => formatNumber(Number(row.original.quantity)),
      },
      {
        accessorKey: "dueDate",
        header: t("executions.columnDate"),
        size: 140,
        enableSorting: true,
        cell: ({ row }) => formatDate(row.original.dueDate),
      },
      {
        id: "timing",
        header: t("executions.columnTiming"),
        size: 130,
        cell: ({ row }) => {
          const diff = row.original.daysUntilDue;
          if (diff < 0) {
            return (
              <span className="font-medium text-red-600">
                {t("executions.daysLate", { count: -diff })}
              </span>
            );
          }
          if (diff === 0) {
            return (
              <span className="text-muted-foreground">
                {t("executions.dueToday")}
              </span>
            );
          }
          return (
            <span className="text-muted-foreground">
              {t("executions.dueIn", { count: diff })}
            </span>
          );
        },
      },
    ],
    [t],
  );

  return (
    <div className="space-y-4">
      <PageHeader title={t("executions.title")} />
      <DataTable<SourceExecution>
        {...ds.tableProps}
        data={query.data?.results ?? []}
        totalCount={query.data?.count ?? 0}
        isLoading={query.isLoading}
        columns={columns}
        searchPlaceholder={t("executions.searchPlaceholder")}
        emptyLabel={t("executions.empty")}
        getRowId={(r) => String(r.id)}
        filterSlot={
          <>
            <DataTableFacetedFilter<OverdueOption>
              mode="single"
              title={t("executions.columnTiming")}
              options={overdueOptions}
              value={overdue}
              onChange={setOverdue}
            />
            <EntityFilter
              title={t("executions.filterItem")}
              descriptor={itemEntity}
              value={producesItem}
              onChange={setProducesItem}
            />
            <EntityFilter
              title={t("executions.filterLocation")}
              descriptor={locationEntity}
              value={location}
              onChange={setLocation}
            />
            <DataTableFacetedFilter
              mode="multi"
              title={t("executions.filterFlavor")}
              options={flavorOptions}
              value={flavor.map((k) => ({
                id: k,
                name: t(`sources.flavor.${k}`),
              }))}
              onChange={(opts) => setFlavor(opts.map((o) => o.id))}
            />
          </>
        }
        onResetFilters={
          overdue || producesItem.length || location.length || flavor.length
            ? () => {
                setOverdue(null);
                setProducesItem([]);
                setLocation([]);
                setFlavor([]);
              }
            : undefined
        }
      />
    </div>
  );
}
